import React, {useRef, useEffect, useState} from 'react'
import {useLocation} from 'react-router'
import clsx from 'clsx'
import {AsideMenuMain} from './AsideMenuMain'
import {
  DrawerComponent,
  MenuComponent,
  ScrollComponent,
  ToggleComponent,
} from '../../../assets/ts/components'
import {getMenuSettings} from '../../../../app/redux/api'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'

type Props = {
  asideMenuCSSClasses: string[]
}

/**
 * The sidebar is the backend's `getfrontendsettings` menu and nothing else (ITOP-652, Kalle
 * 2026-09-29). Every entry, including the two firmware entries under Admin, is a
 * `frontend_menu` row, filtered per role and per user by the backend. The portal adds no entry
 * of its own.
 *
 * Until ITOP-652 the portal appended the two firmware entries itself for Admin, because the
 * backend had no rows for them yet (FE-001 / FE-008 / FE-009). The rows are ITOP-653, and
 * they must exist in an environment before this build is deployed there, or Admins lose both
 * entries. A new page therefore needs a `frontend_menu` row, not an entry here.
 * The 2023 precedent was a46cbf4, which removed a hard-coded "Commands" item the same way.
 *
 * The menu does not gate access. The `role === 'PG'` route registrations in PrivateRoutes and
 * the backend do. A row shown to the wrong role gives a link to a route that is not
 * registered for that role, not access.
 *
 * Defensive about the payload, because this runs before anything validates it: a missing or
 * malformed `menu`, a non-object entry, or a non-array `items` must not take the whole sidebar
 * down. AsideMenuMain maps both levels without a check.
 */
const withMenuArray = (settings: any) => ({
  ...settings,
  menu: (Array.isArray(settings?.menu) ? settings.menu : [])
    .filter((entry: any) => entry && typeof entry === 'object')
    .map((entry: any) =>
      // AsideMenuMain calls `.map` on any truthy `items`. The backend DTO sends a list or null,
      // but the old firmware injection happened to replace a non-array Admin `items` for PG,
      // so removing it must not remove that protection. A non-array becomes "no sub-items".
      entry.items == null || Array.isArray(entry.items) ? entry : {...entry, items: undefined}
    ),
})

const AsideMenu: React.FC<Props> = ({asideMenuCSSClasses}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const role = useSelector<RootState>(({auth}) => auth.role, shallowEqual)
  const [menuAccess, setMenuAccess] = useState<any>({menu: []})
  const {pathname} = useLocation()

  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization()
      DrawerComponent.reinitialization()
      ToggleComponent.reinitialization()
      ScrollComponent.reinitialization()
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0
      }
    }, 50)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  React.useEffect(() => {
    getMenuSettings()
      .then(({data}) => {
        setMenuAccess(withMenuArray(data.Response))
      })
      .catch(() => {})
    // Refetched when the role changes (e.g. a different user signs in): the backend filters the
    // menu per role and per user, so the previous user's menu must not stay on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role])

  return (
    <div
      id='kt_aside_menu_wrapper'
      ref={scrollRef}
      className='hover-scroll-overlay-y my-5 my-lg-5'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-height='auto'
      data-kt-scroll-dependencies='#kt_aside_logo, #kt_aside_footer'
      data-kt-scroll-wrappers='#kt_aside_menu'
      data-kt-scroll-offset='0'
    >
      <div
        id='#kt_aside_menu'
        data-kt-menu='true'
        className={clsx(
          'menu menu-column menu-title-gray-800 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-500',
          asideMenuCSSClasses.join(' ')
        )}
      >
        <AsideMenuMain settings={menuAccess} />
      </div>
    </div>
  )
}

export {AsideMenu}
